import {
  OptimizeGoal,
  OptimizationGoalItem,
  AdgroupConfig,
  AdgroupFormData,
  CampaignFormData,
  VideoDurationType,
  CampaignObjectiveType as Objective,
  VOStatus,
  AppProfilePageType,
  EMEventType,
  InventoryFlow,
  SmartBidType,
  AppItem,
  VOStatusConfig,
  AppEvents,
  AdgroupListItem,
  objectiveOptimizationGoal,
  CreateMode,
} from '@tt4b/ttam-enums';
import {
  useAccountCurrency,
  useAdgroupIos14,
  useAppInfo,
  useStdAdgroupIs,
  useStdObjectiveType,
  useStdAdgroupOptimizationGoal,
  useAdgroupAppEvents,
  useDCAdgroup,
  useStore,
  useStdAdgroupCbo,
  useStdCampaignIs,
  useAdgroupMode,
} from '@tt4b/ttam-hooks';
import { Ref, computed, ref } from '@vue/composition-api';
import { i18n } from './../../../i18n';
import { installWithInAppEvent } from './consts';
import { OptimizationGoalOptionItem } from './types';
import { scopedComputed } from './../../../utils/scopeFn';
import {
  getTargetOptGoalOptions,
  GetObjectiveHandleOptGoalMap,
  handleOptGoalItemExtraOptions,
  useProductSalesLSSOptGoalOptionsGet,
} from './hook/generateOptGoal';

/** Common  */

export interface CommonSelectDisabledProps {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
  dcAdgroupList: Ref<AdgroupListItem[]>;
}

function useCommonSelectDisabled(props: CommonSelectDisabledProps) {
  const { adgroupData, campaignData, adgroupConfig, dcAdgroupList } = props;
  const { isDCHasAdgroup } = useDCAdgroup({
    dcAdgroupList,
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  return {
    disabled: computed(
      () =>
        isDCHasAdgroup.value ||
        // TODO 待splittest支持
        // (isInSplitTestLevel.value && isEngagement.value) ||
        adgroupConfig.value.disableOptimizatioGoal,
    ),
  };
}

/** VideoViewSelect  */
export function useVideoViewSelectPropsOptions() {
  return {
    options: computed(() => [
      {
        label: i18n.t('module_adgroup_optimize_videoview_6s'), // videoview 6s
        value: VideoDurationType.SIX_SECONDS,
      },
      {
        label: i18n.t('module_adgroup_optimize_videoview_2s'), // videoview 2s
        value: VideoDurationType.TWO_SECONDS,
      },
    ]),
  };
}

export function useVideoViewSelectPropsDisabled(props: CommonSelectDisabledProps) {
  const { disabled } = useCommonSelectDisabled(props);

  return {
    disabled,
  };
}

export function useVideoViewSelectPropsPlaceholder() {
  return {
    placeholder: computed(() => i18n.t('home_select_choose')),
  };
}

export function useVideoViewSelectPropsVisible(props: { campaignData: Ref<CampaignFormData> }) {
  const { campaignData } = props;
  const { isVideoView } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  return {
    visible: computed(() => isVideoView.value),
  };
}

/** DefaultSelect  */
export function useDefaultSelectPropsDisabled(props: CommonSelectDisabledProps) {
  const { disabled } = useCommonSelectDisabled(props);

  return {
    disabled,
  };
}

/**
 * 根据条件筛选对应的 Optimization Goal 是否存在
 * 筛选条件：https://bytedance.feishu.cn/wiki/wikcnbMJ8GE2suh4UGmnnKsDJPc#
 * @param list 需要进行过滤的 Optimization Goal list
 */
export function useFilterAvailableOptGoals(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
  voStatus: Ref<VOStatusConfig>;
  appInfo: Ref<AppItem>;
  list: OptimizeGoal[];
}) {
  const { adgroupData, campaignData, list, appEvents, appInfo, voStatus } = props;
  const { currencyOffset } = useAccountCurrency();
  const { isWebsite, isAppDownload, isAppTypeAndroid, isOnlyPangle } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { hasClickTrackUrlOrSdkS2s } = useAppInfo({
    appInfo,
  });
  const { isIOS14Above } = useAdgroupIos14({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { inAppEvents } = useAdgroupAppEvents({
    appEvents,
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const filterShow = (item: OptimizeGoal): boolean => {
    // 非 SHOW 类型不做过滤
    if (item !== OptimizeGoal.SHOW) {
      return true;
    }
    // 仅币种精度为 0.01 时候支持 impression
    // TT 不支持 impression
    if (currencyOffset.value !== 2 || adgroupData?.value?.inventoryFlow?.includes?.(InventoryFlow.TIKTOK)) {
      return false;
    }
    // 落地页绑定 Pixel 时不支持 impression,
    if (isWebsite.value && adgroupData?.value?.adRefPixelId !== '') {
      return false;
    }
    // cbo不支持show
    if (isCBO.value) {
      return false;
    }

    return true;
  };

  const filterInstall = (item: OptimizeGoal): boolean => {
    // 非 Install 和 InAPPEVENT 类型不做过滤。因为过滤逻辑一致，所以这两个函数写在一起
    if (item !== OptimizeGoal.INSTALL && item !== OptimizeGoal.IN_APP_EVENT) {
      return true;
    }
    // 非 APP 类型不支持 install
    // 仅在 app 中包含三方(click track url)的时候支持 install。
    if (!isAppDownload.value || !hasClickTrackUrlOrSdkS2s.value) {
      return false;
    }

    return true;
  };

  const filterInAppEvent = (item: OptimizeGoal): boolean => {
    if (item !== OptimizeGoal.IN_APP_EVENT) {
      return true;
    }

    if (inAppEvents.value?.events?.length === 0) {
      return false;
    }

    return inAppEvents.value.events.some(
      v => v.external_action !== EMEventType.ACTIVE && v.external_action !== EMEventType.NO_ACTIVE,
    );
  };

  const filterValue = (item: OptimizeGoal): boolean => {
    const { isAppInstall } = useStdObjectiveType({
      campaignFormData: campaignData,
    });
    const { isStandardAdsWithCatalog } = useStdAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });

    if (item !== OptimizeGoal.VALUE) {
      return true;
    }
    // APP 下绑定安卓APP 且所选APP具有点击回传链接支持VO
    const isAndroidAppSupported = isAppTypeAndroid.value && hasClickTrackUrlOrSdkS2s.value;
    const isAppSupported = isAndroidAppSupported || (isIOS14Above.value && hasClickTrackUrlOrSdkS2s.value);

    if (isWebsite.value) {
      if (isOnlyPangle.value) {
        return true;
      }

      return voStatus.value?.valueOptimizerStatus !== VOStatus.NOTSUPPORT;
    }

    if (isOnlyPangle.value) {
      if (isStandardAdsWithCatalog.value) {
        return isAppSupported;
      }
      if (!isAppInstall.value || !isAndroidAppSupported) {
        return false;
      }
    }

    return isAppSupported;
  };
  return list.filter(filterShow).filter(filterInstall).filter(filterInAppEvent).filter(filterValue);
}

// eslint-disable-next-line max-lines-per-function
export function useDefaultSelectPropsOptions(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  voStatus?: Ref<VOStatusConfig>;
  dcAdgroupList?: Ref<AdgroupListItem[]>;
  appEvents?: Ref<AppEvents>;
  appInfo?: Ref<AppItem>;
  createMode?: Ref<CreateMode>;
}) {
  const {
    campaignData,
    adgroupData,
    voStatus = ref({}) as Ref<VOStatusConfig>,
    appEvents = ref({}) as Ref<AppEvents>,
    appInfo = ref({}) as Ref<AppItem>,
    createMode,
  } = props;
  const { isLiveAds, isStandardAdsWithShop, isStandardAdsWithShowcase, isProductSales, isPSA } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { supportInstallDeepAppEvents } = useAdgroupAppEvents({
    appEvents,
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isCreateNonFirstCBOAdgroup, firstCboAdgroupData, isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  /**
   * @func generateOptGoalList 生成可选的 Optimization Goal list
   * 参考逻辑：https://bytedance.feishu.cn/wiki/wikcnbMJ8GE2suh4UGmnnKsDJPc -> 不同 Objective 下支持情况梳理
   */
  // eslint-disable-next-line max-lines-per-function
  const generateOptGoalList = (): OptimizationGoalOptionItem[] => {
    const { objectiveType } = campaignData.value;

    if (objectiveType === Objective.NONE) {
      return [];
    }

    // cbo下创建非第一个adgroup，要选中第一个ad的optimization goal
    if (isCreateNonFirstCBOAdgroup.value) {
      return getTargetOptGoalOptions([firstCboAdgroupData.value?.optimizeGoal || OptimizeGoal.NONE]);
    }

    // shop下的og，另一套逻辑
    if (
      isProductSales.value &&
      (isLiveAds.value || isStandardAdsWithShop.value || isStandardAdsWithShowcase.value || isPSA.value)
    ) {
      return useProductSalesLSSOptGoalOptionsGet({
        adgroupData,
        campaignData,
        voStatus,
      });
    }

    /**
     * 下面是通用的生成Optimization Goal的function
     */

    /** 1. 获取对应的OptimizationGoal指定adgroup的optimization goal */
    let list = (objectiveOptimizationGoal[objectiveType] || []) as OptimizeGoal[];

    /** 2. 获取对应的OptimizationGoal的处理函数，并进行处理 */
    list =
      GetObjectiveHandleOptGoalMap[objectiveType]?.({
        adgroupData,
        campaignData,
        voStatus,
        originList: list,
        createMode,
      }) || [];

    /** 3. 处理当前支持的 Optimization Goal */
    list = useFilterAvailableOptGoals({
      adgroupData,
      campaignData,
      list,
      appEvents,
      appInfo,
      voStatus,
    });

    /** 4.将 number[] 转成 select组件的options */
    const optGoalOpts = getTargetOptGoalOptions(list);

    /** 5. 特殊OG添加逻辑 */
    const isAppProfilePageOpen = adgroupData.value.adAppProfilePageType === AppProfilePageType.on;
    if (
      supportInstallDeepAppEvents.value &&
      optGoalOpts.find(optGoal => optGoal.value === OptimizeGoal.INSTALL) &&
      !isAppProfilePageOpen &&
      !isCBO
    ) {
      optGoalOpts.push(installWithInAppEvent as unknown as OptimizationGoalItem);
    }

    /** 6. 补充Options额外的属性，比如labelInfoVisible、disabled */
    return handleOptGoalItemExtraOptions({
      adgroupData,
      campaignData,
      optGoalOpts,
      voStatus,
    });
  };

  const options = scopedComputed(() => generateOptGoalList());

  return {
    options,
  };
}

/** ExplanationHint  */
export function useExplanationTipsPropsTips(props: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = props;
  const { isOptGoalReach, isOptGoalVideoView2s, isOptGoalVideoView6s } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });

  return {
    tips: computed(() => {
      if (isOptGoalReach.value) {
        return i18n.t('adgroup_optimize_reach_hint');
      }
      if (isOptGoalVideoView6s.value) {
        return i18n.t('adgroup_optimize_videoview_hint');
      }
      if (isOptGoalVideoView2s.value) {
        return i18n.t('adgroup_optimize_videoview2s_hint');
      }

      return '';
    }),
  };
}

/** CboSecondGoalTips  */
export function useCboSecondGoalTipsPropsVisble(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = props;
  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  return {
    visible: computed(() => isCBO.value && campaignData.value?.smartBidType === SmartBidType.NO_BID),
  };
}

/** CboNotSupportedTips  */
export function useCboNotSupportedTipsPropsVisible(props: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  optimizationGoalOptions: Ref<OptimizationGoalOptionItem[]>;
}) {
  const { adgroupData, campaignData, optimizationGoalOptions } = props;
  const { isCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  return {
    visible: computed(
      () => isCBO.value && campaignData?.value?.optimizeGoal && !optimizationGoalOptions?.value?.length,
    ),
  };
}

/** AppTrackUrlTips  */
export function useAppTrackUrlTipsPropsVisible(props: {
  appInfo: Ref<AppItem>;
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { appInfo, adgroupData, campaignData } = props;
  const { hasClickTrackUrlOrSdkS2s } = useAppInfo({
    appInfo,
  });

  const { isTraffic } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  const { isAppDownload } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const visible = computed(() => !isTraffic.value && isAppDownload.value && !hasClickTrackUrlOrSdkS2s.value);
  return {
    visible,
  };
}

export function useAppTrackUrlTipsPropsLinkText(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { adgroupData, campaignData } = options;

  const { isStandardAdsWithCatalog } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  return {
    linkText: computed(() => {
      if (isStandardAdsWithCatalog.value) {
        return i18n.t('adgroup_setup_app_tracking_link');
      }
      return i18n.t('module_adgroup_bidding_setup_tracking_operation');
    }),
  };
}

export function useAppTrackUrlTipsPropsLinkUrl(props: { appInfo: Ref<AppItem> }) {
  const { appInfo } = props;
  const { state } = useStore();
  const aadvid = computed(() => state.baseInfo.value.aadvid);
  const { appId4EM } = useAppInfo({
    appInfo,
  });
  return {
    linkUrl: computed(() => `/i18n/events_manager/app/detail/${appId4EM.value}?aadvid=${aadvid.value}`),
  };
}

export function useAppTrackUrlTipsPropsText(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { adgroupData, campaignData } = options;

  const { isStandardAdsWithCatalog, isProductRetargeting } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  return {
    text: computed(() => {
      if (isStandardAdsWithCatalog.value) {
        return isProductRetargeting.value
          ? i18n.t('module_adgroup_app_retarget_tracking_hint')
          : i18n.t('module_adgroup_app_install_tracking_hint');
      }
      return i18n.t('module_adgroup_bidding_setup_tracking_hint');
    }),
  };
}

/** LabelHintVisible  */
export function useLabelHintPropsVisible(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appInfo: Ref<AppItem>;
}) {
  const { appInfo, adgroupData, campaignData } = props;
  const { hasClickTrackUrlOrSdkS2s } = useAppInfo({
    appInfo,
  });

  const { isOptGoalClick } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });

  const { isConversion, isAppInstall } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  return {
    visible: computed(
      () => isConversion.value || (isAppInstall.value && (!isOptGoalClick.value || !hasClickTrackUrlOrSdkS2s.value)),
    ),
  };
}

/** IOS14DisabedHint  */
export function useIos14DisabeldHintPropsDisabled(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  dcAdgroupList: Ref<AdgroupListItem[]>;
}) {
  const { adgroupData, campaignData, dcAdgroupList } = props;
  const { isDCHasNoAdgroup } = useDCAdgroup({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
    dcAdgroupList,
  });

  return {
    disabled: computed(() => !isDCHasNoAdgroup.value),
  };
}

/** 初始值获取  */
export function useInitDataGet(props: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { adgroupData, campaignData } = props;

  const { isProductSalesCloseLoop } = useStdCampaignIs({
    campaignFormData: campaignData,
  });

  const { isCBO } = useStdAdgroupCbo({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  const { isVideoView } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  const deepExternalAction = computed(() => {
    if (isCBO.value && campaignData.value.smartBidType === SmartBidType.NO_BID) {
      return EMEventType.NO_ACTIVE;
    }
    return adgroupData.value.deepExternalAction;
  });

  const optimizedGoalData: {
    [K in Objective]?: { optimizeGoal: OptimizeGoal; cpvVideoDurationType?: VideoDurationType };
  } = {
    [Objective.TRAFFIC]: { optimizeGoal: OptimizeGoal.CLICK },
    [Objective.APPINSTALL]: {
      // 如果选择了 CBO，初始值和 CBO campaign 中的 optimizeGoal 同步
      // 注意逻辑：如果 CBO 下第二个 AdGroup，选择和之前一样的 Optimization Goal
      optimizeGoal: isCBO.value ? campaignData.value.optimizeGoal : OptimizeGoal.CLICK,
    },
    [Objective.CONVERSION]: { optimizeGoal: OptimizeGoal.CONVERT },
    [Objective.VIDEOVIEW]: {
      optimizeGoal: OptimizeGoal.VIDEO_VIEW,
      cpvVideoDurationType: VideoDurationType.SIX_SECONDS,
    },
    [Objective.REACH]: { optimizeGoal: OptimizeGoal.REACH },
    [Objective.DPA]: { optimizeGoal: OptimizeGoal.CLICK },
    [Objective.RF]: { optimizeGoal: OptimizeGoal.REACH },
    [Objective.LEADADS]: { optimizeGoal: OptimizeGoal.LEAD },
    [Objective.ENGAGEMENT]: { optimizeGoal: OptimizeGoal.WEBVIEWS },
    [Objective.PRODUCT_SALES]: {
      optimizeGoal: isProductSalesCloseLoop.value ? OptimizeGoal.NONE : OptimizeGoal.CLICK,
    },
  };
  const optimizeGoal = computed(
    () => optimizedGoalData[campaignData.value.objectiveType]?.optimizeGoal || OptimizeGoal.NONE,
  );

  const cpvVideoDurationType = computed(() =>
    isVideoView.value ? VideoDurationType.SIX_SECONDS : VideoDurationType.NONE,
  );

  return {
    // externalAction 原样返回
    externalAction: adgroupData.value.externalAction,
    deepExternalAction: deepExternalAction.value,
    optimizeGoal: optimizeGoal.value,
    cpvVideoDurationType: cpvVideoDurationType.value,
  };
}
